import React, { useEffect, useState } from 'react';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Select from 'react-select';
import Chart from 'react-google-charts';
import { API } from '../../../api/API';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { useNavigate } from 'react-router-dom';
import CommonTableNew from '../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew';
import Loader from '../../../Layout/Loader';

export const MonthComponent: React.FC<{ month: { [key: string]: string }, employeeId: number, dropdownLabel: string }> = ({ month, employeeId, dropdownLabel }) => {
  const [expenseData, setExpenseData] = useState<Array<any>>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDataForMonth = async () => {
      const serverUrl = localStorage.getItem('serverUrl');
      const monthEntries = Object.entries(month);
      const fetchedData: Array<any> = [];

      for (const [range] of monthEntries) {
        try {
          const formattedMonth = range.split('|')[0].slice(5, 7) + '-' + range.split('|')[0].slice(0, 4); // Convert to 'mm-yyyy'
          const url = `${serverUrl}${API.expenseOverview}`;
          const params = {
            employee_id: employeeId,
            moye: formattedMonth,
          };
          const { status, data } = await getData(url, true, params);

          if (status === 200) {
            fetchedData.push({ range, data: data.data });
            console.log(`Expense Overview API Response for ${range}:`, data);
          } else {
            console.error(`Failed to fetch expense overview data for ${range}.`);
          }
        } catch (error) {
          console.error(`Error fetching expense overview data for ${range}:`, error);
        }
      }

      setExpenseData(fetchedData);
    };

    fetchDataForMonth();
  }, [month, employeeId]);

  const openExpensesPage = (clickedMonth: string, totalExpenseAmount: number) => {
    navigate('/expenses', {
      state: {
        month: { [clickedMonth]: month[clickedMonth] },
        employeeId,
        dropdownLabel,
        totalExpenseAmount, // Pass TotalExpenseAmount as a new parameter
        backPath: '/Approvals',
        backState: { activeTab: '3' },
      },
    });
  };

  const renderPieCharts = (data: any, range: string) => {
    const pieChartData = [
      {
        title: 'Working',
        data: [
          ['Category', 'Value'],
          ['HQ', data.Working.TotalHQ],
          ['ExHQ', data.Working.TotalExHQ],
          ['Los', data.Working.TotalLos],
          ['Os', data.Working.TotalOs],
        ],
      },
      {
        title: 'Attendance',
        data: [
          ['Category', 'Value'],
          ['Working Days', data.Attendance.TotalWorkingDays],
          ['FiedWork', data.Attendance.TotalFiedWork],
          ['Leaves', data.Attendance.TotalLeaves],
          ['Holidays', data.Attendance.TotalHolidays],
        ],
      },
      {
        title: 'Expense',
        data: [
          ['Category', 'Value'],
          ['Expenses', data.Expense.TotalExpenseAmount],
          ['Ta', data.Expense.TotalTaAmount],
          ['Da', data.Expense.TotalDaAmount],
          ['Other', data.Expense.TotalOtherAmount],
        ],
      },
    ];

    const formattedMonth = new Date(range.split('|')[0]).toLocaleString('default', {
      month: 'short',
      year: 'numeric',
    });
    //code of formatted month like dec 2025 pie chart
    return (
      <div style={{ marginBottom: '20px', border: '1px solid #ccc', borderRadius: '8px', padding: '10px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '10px' }}>
          <h4 style={{ textAlign: 'left', margin: 0 }}>{formattedMonth}</h4> {/* // Displayed formatted month like dec 2025 */}

          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textAlign: 'left', margin: 0, color: 'blue', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              openExpensesPage(range, data.Expense.TotalExpenseAmount);
            }}
          >
            View
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          {pieChartData.map((chart, index) => {
            const allZero = chart.data.slice(1).every(([, value]) => value === 0);
            return (
              <div
                key={index}
                style={{
                  width: '30%',
                  borderLeft: index === 1 ? '1px solid #ddd' : 'none',
                  borderRight: index === 1 ? '1px solid #ddd' : 'none',
                  padding: index === 1 ? '5px' : '0',
                }}
              >
                <h5 style={{ textAlign: 'left' }}>{chart.title}</h5>

                <Chart
                  chartType="PieChart"
                  data={allZero ? [['Category', 'Value'], ['No Data', 1]] : chart.data}
                  options={{
                    pieHole: 0.6,
                    legend: { position: 'bottom' },
                    pieSliceText: 'none',
                    slices: allZero
                      ? { 0: { color: '#d3d3d3' } } // Grey color for no data
                      : {
                        0: { color: '#338bff' },
                        1: { color: '#1c65d3' },
                        2: { color: '#0800fc' },
                        3: { color: '#85cffd' },
                      },
                  }}
                  height="200px"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {expenseData.map(({ range, data }) => (
        <div key={range}>
          {renderPieCharts(data, range)}
        </div>
      ))}
    </div>
  );
};

const ExpenseApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [allowedMonths, setAllowedMonths] = useState<Array<{ [key: string]: string }> | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{ EmployeeId: number; FirstName: string; LastName: string; EmployeeCode: string; Designations: { Designation: string } }> | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState<string>('');
  const [designationFilter, setDesignationFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState("Select Month");

  const toggle = () => setOpen(!open);
  useEffect(() => {
    const fetchAllowedMonths = async () => {
      try {
        const serverUrl = localStorage.getItem('serverUrl');
        const url = `${serverUrl}${API.getAllowedMonths}`;
        const { status, data } = await getData(url, true);

        if (status === 200 && Array.isArray(data.data) && data.data.length > 0) {
          setAllowedMonths(data.data);
          console.log('Allowed Months API Response:', data.data);
        } else {
          console.error('Failed to fetch allowed months or no data available.');
        }
      } catch (error) {
        console.error('Error fetching allowed months:', error);
      } finally {
        setIsLoading(false); // Set loading to false after API call
      }
    };

    const fetchTeamMembers = async () => {
      try {
        const serverUrl = localStorage.getItem('serverUrl');
        const url = `${serverUrl}${API.getMyTeam}`;
        const { status, data } = await getData(url, true);

        if (status === 200 && data?.data?.team?.length > 0) {
          setTeamMembers(data?.data?.team);
          console.log('Team Members API Response:=====', data?.data?.team);
        } else {
          console.error('Failed to fetch team members or no data available.');
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setIsLoading(false); // Set loading to false after API call
      }
    };

    fetchAllowedMonths();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      setSelectedEmployeeId(teamMembers[0].EmployeeId);
    }
  }, [teamMembers]);

  const filteredTeamMembers = React.useMemo(() => {
  if (!teamMembers) return [];

  const q = filterText.trim().toLowerCase();

  return teamMembers.filter((m) => {
    const matchesName =
      !q ||
      `${m.FirstName} ${m.LastName}`.toLowerCase().includes(q);

    const matchesDesignation =
      !designationFilter ||
      m.Designations?.Designation === designationFilter;

    const matchesEmpCode =
      !q ||
      String(m?.EmployeeCode ?? "")
        .toLowerCase()
        .includes(q);

return (matchesName || matchesEmpCode) && matchesDesignation;
  });
}, [teamMembers, filterText, designationFilter]);


  useEffect(() => {
    if (filteredTeamMembers.length === 0) {
      setSelectedEmployeeId(null);
      return;
    }
    if (!selectedEmployeeId || !filteredTeamMembers.some(m => m.EmployeeId === selectedEmployeeId)) {
      setSelectedEmployeeId(filteredTeamMembers[0].EmployeeId);
    }
  }, [filteredTeamMembers]);

  const teamOptions = filteredTeamMembers?.map((member) => ({
    value: member.EmployeeId,
    label: `${member.FirstName} ${member.LastName}`,
  }));

  const monthsList = React.useMemo(() => {
    if (!allowedMonths) return [];
    return allowedMonths.flatMap(obj => Object.entries(obj).map(([range, label]) => ({ range, label })));
  }, [allowedMonths]);

  useEffect(() => {
    if (monthsList.length > 0 && !selectedValue) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      
      // Find current month in the API list
      const currentMonthObj = monthsList.find(m => {
        const rangeStart = m.range.split('|')[0];
        return rangeStart.startsWith(`${currentYear}-${currentMonth}`);
      });

      if (currentMonthObj) {
        setSelectedValue(currentMonthObj.range);
        setSelectedLabel(currentMonthObj.label);
      }
    }
  }, [monthsList, selectedValue]);

  const selectedMonthObj = selectedValue
    ? { [selectedValue]: monthsList.find(m => m.range === selectedValue)?.label ?? '' }
    : null;

  return (
    <div style={{}} className='px-3 py-1'>
      {isLoading ? (
        <Loader height="300px" />
      ) : (
        <>
          {teamOptions && (
            <div className='d-flex justify-content-start align-items-center flex-wrap' style={{ marginBottom: '10px', gap: '10px' }}>
              <div style={{ width: '280px' }}>
                <Select
                  options={teamOptions || []}
                  value={teamOptions?.find((t) => t.value === selectedEmployeeId) || null}
                  onChange={(opt: any) => {
                    if (opt) {
                      setSelectedEmployeeId(opt.value);
                    }
                  }}
                  isSearchable={true}
                  placeholder="Search Team Member..."
                  className="text-dark fw-semibold"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: '#dee2e6',
                      minHeight: '38px',
                      color: '#000000',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#000000',
                      fontWeight: '600',
                    }),
                    option: (base, state) => ({
                      ...base,
                      color: '#000000',
                      backgroundColor: state.isFocused ? '#e9ecef' : '#ffffff',
                    }),
                  }}
                />
              </div>
              <input
                type="text"
                className="form-control border"
                placeholder="Filter by name..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{ width: '200px' }} 
              />
              <select
                className="form-control border"
                value={designationFilter}
                onChange={(e) => setDesignationFilter(e.target.value)}
                style={{ width: '180px' }}
              >
                <option value="">All Designations</option>
                {Array.from(
                  new Set(teamMembers?.map((m) => m.Designations?.Designation).filter(Boolean))
                ).map((designation, index) => (
                  <option key={index} value={designation}>
                    {designation}
                  </option>
                ))}
              </select>
            
               <Dropdown isOpen={open} toggle={toggle}>
      <DropdownToggle caret>{selectedLabel}</DropdownToggle>
      <DropdownMenu>
        {monthsList.map((m) => (
          <DropdownItem
            key={m.range}
            onClick={() => {
              setSelectedValue(m.range);
              setSelectedLabel(m.label);
              console.log("Selected Value:", m.range);
            }}
          >
            {m.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
            </div>
          )}
          
          <CommonTableNew
            caption=""
            headData={[
              {
                id: "1",
                head: "Employee Code",
                key: "",
                sortKey: "",
              },
              {
                id: "2",
                head: "Team Members",
                key: "EmployeeName",
                sortKey: "EmployeeName",
              },
              {
                id: "3",
                head: "Designation",
                key: "GradeName",
                sortKey: "GradeName",
              },
              
              { id: "4",
                head: "months",
                key: "months"  ,
                sortKey: "months",
              },
              { id: "5", 
                head: "Action", 
                key: "Action" ,
                sortKey: "Action",
              },
            ]}
            data={filteredTeamMembers || []} // Use filtered data here
            renderRow={(row, rowIndex) => (
              <tr key={rowIndex}>
                <td>{`${row.EmployeeCode}`}</td>
                <td>{`${row?.FirstName || ''} ${row?.LastName || ''}`}</td>
                <td>{`${row?.Designations?.Designation || ''}`}</td>
                <td>{selectedLabel !== "Select Month" ? selectedLabel : "-"}</td>
                <td>
                  <button
                    className='btn btn-primary'
                    type="button"
                    disabled={!selectedValue}
                    onClick={() => {
                      if (!selectedValue) return;
                      navigate('/expenses', {
                        state: {
                          month: selectedMonthObj || { [selectedValue]: monthsList.find(m => m.range === selectedValue)?.label ?? '' },
                          employeeId: row.EmployeeId,
                          dropdownLabel: `${row.FirstName} ${row.LastName}`,
                          totalExpenseAmount: 0,
                          backPath: '/Approvals',
                          backState: { activeTab: '3' },
                        },
                      });
                    }}
                  >
                    View
                  </button>
                </td>

                </tr>
            )}
          />

          {/* {selectedMonthObj && selectedEmployeeId ? (
            <MonthComponent
              month={selectedMonthObj}
              employeeId={selectedEmployeeId}
              dropdownLabel={teamOptions?.find(option => option.value === selectedEmployeeId)?.label || ''}
            />
          ) : (
            allowedMonths && selectedEmployeeId ? (
              <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>Please select a month</div>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>No data found</div>
            )
          )} */}
        </>
      )}
    </div>
  );


};


export default ExpenseApprovals;
